import {
  Box,
  ButtonGroup,
  Heading,
  IconButton,
  Pagination,
  Table,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react-v3";
import { Tooltip } from "../../../ui/tooltip";
import { useState, useMemo, useEffect } from "react";
import { FiArrowDown, FiArrowUp, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { HighlightedText } from "./highlighted-text";
import { sortEmptyLast } from "../utils/sorting-utils";

export type ColumnDef<T> = {
  key: keyof T;
  header: string;
  render?: (row: T, filterQuery: string) => React.ReactNode;
  sortable?: boolean;
  getSortValue?: (row: T) => string | number | Date;
  sort?: "asc" | "desc";
  width?: string | object;
  maxWidth?: string | object;
  minWidth?: string | object;
  handleCellClick?: (e: React.MouseEvent<HTMLTableCellElement>) => void;
  /** Hide this column on small screens (below md breakpoint ~768px) */
  isHiddenOnSmallScreen?: boolean;
  isAlwaysTooltip?: boolean;
};

export type BaseTableLayoutProps<T> = {
  title: string;
  rows: T[];
  columns: ColumnDef<T>[];
  filterQuery: string;
  onRowClick: (row: T, event?: React.MouseEvent) => void;

  // Layout slots
  headerActionsSlot?: React.ReactNode;
  getRowStyle?: (row: T) => Record<string, unknown>;
  isRowViewed?: (row: T) => boolean;
};

/** Styles for sortable column header buttons */
const sortableHeaderButtonStyles = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  px: 2,
  py: 2,
  borderRadius: "md",
  transition: "all 0.15s ease",
  width: "100%",
  _hover: {
    bg: "interactive",
  },
  _active: {
    bg: "interactive.hover",
  },
} as const;

/**
 * Base table layout component for patient overview tables.
 * Provides consistent styling and structure while allowing customization through slots and render props.
 * Card wrapper styling is applied at the parent level.
 *
 * @example Simple table
 * <BaseTableLayout
 *   title="Allergies"
 *   rows={allergyRows}
 *   columns={[
 *     { key: "allergy", header: "Allergy" },
 *     { key: "manifestation", header: "Manifestation" }
 *   ]}
 *   filterQuery={filterQuery}
 *   onRowClick={handleRowClick}
 * />
 */
export function BaseTableLayout<T extends { id: string }>({
  title,
  rows,
  columns,
  filterQuery,
  onRowClick,
  headerActionsSlot,
  getRowStyle,
  isRowViewed,
}: BaseTableLayoutProps<T>) {
  // Determine if we're on a small screen (below md breakpoint)
  const isSmallScreen = useBreakpointValue({ base: true, md: false }, { ssr: false }) ?? false;

  // Filter columns based on screen size
  const visibleColumns = useMemo(() => {
    if (isSmallScreen) {
      return columns.filter(col => !col.isHiddenOnSmallScreen);
    }
    return columns;
  }, [columns, isSmallScreen]);

  const defaultSort = useMemo(() => {
    const defaultColumn = visibleColumns.find(col => col.sort);
    return defaultColumn && defaultColumn.sort
      ? { key: defaultColumn.key, direction: defaultColumn.sort as "asc" | "desc" }
      : { key: null, direction: null };
  }, [visibleColumns]);

  const [sortConfig, setSortConfig] = useState<{
    key: keyof T | null;
    direction: "asc" | "desc" | null;
  }>(defaultSort);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  /** Fixed row height for stable layout */
  const rowHeight = 52; // px
  const minTableBodyHeight = pageSize * rowHeight;

  /** Reset sort to default when filter query changes */
  useEffect(() => {
    setSortConfig(defaultSort);
  }, [filterQuery]);

  /** Validate sort column still exists when columns change */
  useEffect(() => {
    if (sortConfig.key) {
      const columnStillExists = visibleColumns.some(col => col.key === sortConfig.key);
      if (!columnStillExists) {
        setSortConfig(defaultSort);
      }
    }
  }, [visibleColumns, sortConfig.key, defaultSort]);

  /** Reset to page 1 when filter query or sort changes */
  useEffect(() => {
    setCurrentPage(1);
  }, [filterQuery, sortConfig, rows.length]);

  /** Sort rows based on current sort configuration */
  const sortedRows = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) {
      return rows;
    }

    const sortedArray = [...rows];
    const column = columns.find(col => col.key === sortConfig.key);

    sortedArray.sort((a, b) => {
      const sortKey = sortConfig.key;
      if (!sortKey) return 0;

      const aValue = column?.getSortValue ? column.getSortValue(a) : a[sortKey];
      const bValue = column?.getSortValue ? column.getSortValue(b) : b[sortKey];

      /** Handle empty/placeholder values - always push to end regardless of sort direction */
      const emptySort = sortEmptyLast(aValue, bValue);
      if (emptySort !== undefined) return emptySort;

      /** Handle different types */
      let comparison = 0;
      if (aValue instanceof Date && bValue instanceof Date) {
        comparison = aValue.getTime() - bValue.getTime();
      } else if (typeof aValue === "number" && typeof bValue === "number") {
        comparison = aValue - bValue;
      } else {
        /** String comparison */
        const aString = String(aValue).toLowerCase();
        const bString = String(bValue).toLowerCase();
        comparison = aString.localeCompare(bString);
      }

      return sortConfig.direction === "asc" ? comparison : -comparison;
    });

    return sortedArray;
  }, [rows, sortConfig, columns]);

  /** Paginate the sorted rows */
  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedRows.slice(startIndex, endIndex);
  }, [sortedRows, currentPage, pageSize]);

  /** Create empty filler rows to maintain consistent table height */
  const fillerRowsCount = useMemo(() => {
    if (rows.length <= pageSize) return 0;
    return pageSize - paginatedRows.length;
  }, [rows.length, pageSize, paginatedRows.length]);

  function handleColumnHeaderClick(columnKey: keyof T) {
    setSortConfig(prev => {
      if (prev.key !== columnKey) {
        /** New column - start with ascending */
        return { key: columnKey, direction: "asc" };
      }
      /** Same column - cycle through asc → desc → null */
      if (prev.direction === "asc") {
        return { key: columnKey, direction: "desc" };
      }
      if (prev.direction === "desc") {
        return { key: null, direction: null };
      }
      return { key: columnKey, direction: "asc" };
    });
  }

  const isEmptyTable = rows.length === 0;

  return (
    <Box>
      {/* Header with flexible actions slot */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={6}>
        <Heading textStyle="heading.md" color="fg">
          {title} ({rows.length})
        </Heading>
        {headerActionsSlot}
      </Box>

      {isEmptyTable ? (
        <EmptyTable />
      ) : (
        <Table.Root size="sm" variant="line" width="100%" tableLayout="fixed">
          <Table.Header bg="bg.canvas">
            <Table.Row>
              {visibleColumns.map(col => {
                const isSortable = col.sortable !== false;
                const isSorted = sortConfig.key === col.key;
                const sortDirection = isSorted ? sortConfig.direction : null;

                return (
                  <Table.ColumnHeader
                    key={String(col.key)}
                    bg="bg.canvas"
                    fontWeight="bold"
                    textStyle="table.cell"
                    color="fg"
                    py={2}
                    px={2}
                    _first={{ borderTopLeftRadius: "md" }}
                    _last={{ borderTopRightRadius: "md" }}
                    userSelect="none"
                    maxW={col.maxWidth}
                    minW={col.minWidth}
                    width={col.width}
                  >
                    <Box
                      {...(isSortable && sortableHeaderButtonStyles)}
                      cursor={isSortable ? "pointer" : "default"}
                      onClick={isSortable ? () => handleColumnHeaderClick(col.key) : undefined}
                    >
                      {col.header}
                      {isSortable && (
                        <Box display="inline-flex" opacity={isSorted ? 1 : 0.3}>
                          {sortDirection === "asc" && <FiArrowUp size={14} />}
                          {sortDirection === "desc" && <FiArrowDown size={14} />}
                          {!sortDirection && <FiArrowUp size={14} />}
                        </Box>
                      )}
                    </Box>
                  </Table.ColumnHeader>
                );
              })}
            </Table.Row>
          </Table.Header>
          <Table.Body height={rows.length > pageSize ? `${minTableBodyHeight}px` : undefined}>
            {paginatedRows.map(row => {
              const isViewed = isRowViewed ? isRowViewed(row) : true;
              return (
                <Table.Row
                  key={row.id}
                  cursor="pointer"
                  onClick={(e: React.MouseEvent) => onRowClick(row, e)}
                  _hover={{ bg: "interactive.hover" }}
                  borderBottom="1px"
                  borderColor="border.subtle"
                  boxShadow={
                    isViewed
                      ? "inset 3px 0 0 0 var(--chakra-colors-gray-300)"
                      : "inset 3px 0 0 0 var(--chakra-colors-brand-solid)"
                  }
                  opacity={isViewed ? 0.6 : 1}
                  height={`${rowHeight}px`}
                  maxHeight={`${rowHeight}px`}
                  {...getRowStyle?.(row)}
                >
                  {visibleColumns.map(col => (
                    <Table.Cell
                      key={String(col.key)}
                      textStyle="table.cell"
                      py={3}
                      px={4}
                      color="fg.muted"
                      maxW={col.maxWidth}
                      minW={col.minWidth}
                      width={col.width}
                      maxHeight={`${rowHeight}px`}
                      onClick={col.handleCellClick}
                    >
                      <Tooltip
                        {...(col.render
                          ? { content: undefined, disabled: true }
                          : {
                              content: String(row[col.key]),
                              disabled: !isSmallScreen && !col.isAlwaysTooltip,
                            })}
                      >
                        <Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                          {col.render ? (
                            col.render(row, filterQuery)
                          ) : (
                            <HighlightedText
                              text={String(row[col.key])}
                              searchQuery={filterQuery}
                            />
                          )}
                        </Box>
                      </Tooltip>
                    </Table.Cell>
                  ))}
                </Table.Row>
              );
            })}

            {/* Empty filler rows to maintain consistent height on last page */}
            {fillerRowsCount > 0 &&
              Array.from({ length: fillerRowsCount }).map((_, index) => (
                <Table.Row
                  key={`filler-${index}`}
                  height={`${rowHeight}px`}
                  maxHeight={`${rowHeight}px`}
                  borderBottom="1px"
                  borderColor="border.subtle"
                  pointerEvents="none"
                >
                  {visibleColumns.map(col => (
                    <Table.Cell key={String(col.key)} height={`${rowHeight}px`} />
                  ))}
                </Table.Row>
              ))}
          </Table.Body>
        </Table.Root>
      )}

      {/* Pagination - only show if there are more items than page size */}
      {!isEmptyTable && rows.length > pageSize && (
        <Box
          pt={2}
          pb={2}
          px={2}
          borderBottomRadius="md"
          borderWidth="0 1px 1px 1px"
          borderStyle="solid"
          borderColor="border.DEFAULT"
          display="flex"
          justifyContent="flex-end"
          backgroundColor="white"
        >
          <Pagination.Root
            count={rows.length}
            pageSize={pageSize}
            page={currentPage}
            onPageChange={(e: { page: number }) => setCurrentPage(e.page)}
          >
            <ButtonGroup variant="ghost" size="sm" wrap="wrap" bg="white">
              <Pagination.PrevTrigger asChild>
                <IconButton>
                  <FiChevronLeft />
                </IconButton>
              </Pagination.PrevTrigger>

              <Pagination.Items
                render={page => (
                  <IconButton variant={{ base: "ghost", _selected: "outline" }}>
                    {page.value}
                  </IconButton>
                )}
              />

              <Pagination.NextTrigger asChild>
                <IconButton>
                  <FiChevronRight />
                </IconButton>
              </Pagination.NextTrigger>
            </ButtonGroup>
          </Pagination.Root>
        </Box>
      )}
    </Box>
  );
}

/**
 * Displays a placeholder when there are no rows for a table
 */
function EmptyTable() {
  return (
    <Box py={1}>
      <Text color="fg.muted">No results found</Text>
    </Box>
  );
}
